import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import {
  OAuthService,
  OAuthEvent,
  AuthConfig,
} from 'angular-oauth2-oidc';
import { AppService } from './app.service';
import { IConfig } from './shared/config';
import { map, Observable, of, switchMap } from 'rxjs';
import { Event, NavigationEnd } from '@angular/router';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { EntitlementService } from './shared/entitlement.service';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { HttpClient } from '@angular/common/http';
import * as jwt_decode from "jwt-decode";
import { ProfileSelectionService } from './profile-selection.service';
import { CompanyService } from './company/company.service';


@Injectable({
  providedIn: 'root',
})
export class PrfAuthService {
  appConfig: IConfig;
  authConfig: AuthConfig = { waitForTokenInMsec: 10 };
  errorMessage: any;
  private renderer: Renderer2;
  public isonetFeature: Observable<any>;
  private deepLinkURL: string;

  constructor(
    private oauthService: OAuthService,
    private AppService: AppService,
    private entitlementService: EntitlementService,
    private http: HttpClient,
    private cookieService: CookieService,
    private rendererFactory: RendererFactory2,
    private profileSelectionService: ProfileSelectionService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    cookieService.set('prd_ctx', environment.ProductIdAlias);
    this.setAuthConfig(false);
    this.isonetFeature = this.http.get(`${environment.isonetFeatureFlagApi}`).pipe(map(x => x['featureFlag']))
    this.loadCookieConsentScript();
  }

  initAuth() {
    console.log('initAuth called');
  }

  accessToken(): string {
    return this.oauthService.getAccessToken();
  }

  setAuthConfig(domainChange) {
    this.AppService.getConfigStatus().subscribe({
      next: (configStatus) => {
        if (configStatus) {
          this.appConfig = this.AppService.getGlobalConfig();
          this.authConfig.issuer = this.appConfig.oktaIssuer;
          this.authConfig.redirectUri = this.appConfig.redirectUrl;
          this.authConfig.responseType = 'code';
          this.authConfig.clientId = this.appConfig.clientId;
          this.authConfig.scope = 'openid profile email';
          this.authConfig.postLogoutRedirectUri =
            this.appConfig.oktaPostLogoutRedirectUrl;
          this.authConfig.logoutUrl = this.appConfig.gatewayLoginUrl + this.appConfig.oktaPostLogoutRedirectUrl;
          this.configureWithConfigApi();
          console.log('setAuthConfig configured');
        }
      },
      error: (error) => (this.errorMessage = error as any)
    });
  }

  private configureWithConfigApi() {
    this.oauthService.setStorage(sessionStorage);
    this.oauthService.configure(this.authConfig);
    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      this.oauthService.tokenValidationHandler = new JwksValidationHandler();
    });
  }

  getDecodedAccessToken(token: string): any {
    try {
      return jwt_decode.jwtDecode(token);
    } catch (err) {
      return null;
    }
  }


  addUserIdAndTokenToCookies() {
    const decoded_access_token = this.getDecodedAccessToken(this.oauthService.getAccessToken());
    let userId = decoded_access_token.sub;
    if (!userId.toLowerCase().endsWith("@verisk.com")) {
      userId = decoded_access_token.email;
    }
    console.log("okta-aware 3/14 UserId added to cookie : " + userId);
    const expiryTime = this.oauthService.getAccessTokenExpiration();
    this.cookieService.set("access_token", this.oauthService.getAccessToken(), expiryTime, "/", window.location.hostname, true, 'Strict');
    this.cookieService.set("userid", userId, expiryTime, "/", window.location.hostname, true, 'Strict');
  }

  homeSignOut(homeSignoutUrl: string) {
    console.log("redirecting to home signout");
    sessionStorage.setItem('redirectToGatewayLogout', 'false');
    window.location.href = homeSignoutUrl;
  }

  public signout(): void {
    const cookies = this.cookieService.getAll();
    for (const cookieName in cookies) {
      if (!cookieName.startsWith('Optanon')) {
        this.cookieService.delete(cookieName, '/', ".verisk.com");
      }
    }
    this.cookieService.set("global_logout", "true", 1, "/", ".verisk.com", true, 'Strict');
    this.entitlementService.setLoginStatus(false);
    this.AppService.getConfigStatus().subscribe({
      next: _configStatus => {
        if (_configStatus) {
          const _appConfig = this.AppService.getGlobalConfig();
          this.homeSignOut(_appConfig.logoutUrl);
        }
      }
    });
  }


  updateCtxCstd() {
    const cstid = localStorage.getItem('cstid');
    if (cstid != this.cookieService.get('ctx_cstid')) {
      this.cookieService.set('ctx_cstid', cstid, 1, "/", window.location.hostname, true, 'Strict');
    }
  }


  setupProfileSelectAndEligibility() {
    this.AppService.getConfigStatus().subscribe(_configStatus => {
      if (_configStatus) {
        const _appConfig = this.AppService.getGlobalConfig();
        this.profileSelectionService.setProductContextCookie();
        this.updateCtxCstd();
        this.profileSelectionService.hasCompanyAccess().pipe(
          switchMap(hasAccess => {
            if (hasAccess === false) {
              this.createDeepLinkUrl();

              return of(false); // If the user doesn't have company access, return an observable that completes immediately
            }
            return this.profileSelectionService.includeIdealUX();
          }),
          switchMap((hasCompanyAccess) => {
            if (hasCompanyAccess === false) {
              return of(false);
            }
            return this.AppService.isProductEligible
          })
        )
          .subscribe({
            next: result => {
              this.createDeepLinkUrl();
            },
            error: error => {
              this.errorMessage = error;
            }
          }
          );
      }
    });
    this.setAuthConfig(false);


  }


  createDeepLinkUrl() {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    // Remove the redirectedFromMenu query parameter
    params.delete('redirectedFromMenu');

    // Update the URL with the modified query parameters
    url.search = params.toString();
    this.deepLinkURL = url.toString();
  }


  // handle navigation endsession
  checkRouterEvent(routerEvent: Event) {
    if (routerEvent instanceof NavigationEnd) {
      if (sessionStorage.getItem('redirectToGatewayLogout') == 'true') {
        this.signout();
        return;
      }

      if (
        this.oauthService.getIdToken() &&
        this.oauthService.hasValidAccessToken()
      ) {
        if (this.oauthService.getAccessToken() != this.cookieService.get("access_token")) {
          this.addUserIdAndTokenToCookies();
          console.log("okta-aware cookie set mismatch");
          location.reload();
        }
        var globalLogout = this.cookieService.get("global_logout")
        if (globalLogout == "true") {
          const ticket = sessionStorage.getItem('access_token');
          ticket && this.callAppLogout(ticket);
          this.signout();
          return;
        }
        const isAuthorized = this.oauthService.hasValidAccessToken();
        console.log('hasVaalidAccessToken: ' + isAuthorized);
        this.entitlementService.setLoginStatus(true);
        this.setupProfileSelectAndEligibility();
      } else if (
        window.location.href
          .toLowerCase()
          .indexOf('.verisk.com/auth/oktaloginflow') >= 0
      ) {
        this.entitlementService.setLoginStatus(false);
        console.log('redirecting to sso');
        this.oauthService.initCodeFlow();
        this.setAuthConfig(false);
      } else if (
        window.location.href
          .toLowerCase()
          .indexOf('.verisk.com/auth/callback') >= 0
      ) {
        this.entitlementService.setLoginStatus(false);
        this.cookieService.set("global_logout", "false", 1, "/", ".verisk.com", true, 'Strict');
        this.oauthService.events.subscribe(({ type }: OAuthEvent) => {
          switch (type) {
            case 'token_received':
              this.addUserIdAndTokenToCookies();
              let url = sessionStorage.getItem('deeplink');
              if (url == null) {
                url = ""
              }
              sessionStorage.removeItem('deeplink');
              window.location.href = url;
              break;
          }
        });
        this.setAuthConfig(true);
      } else {
        this.setAuthConfig(false);
        console.log('redirecting to gateway');
        if (window.location.pathname !== '/' && window.location.pathname !== '/index.html') {
          sessionStorage.setItem("deeplink", window.location.href);
        }
        this.redirectToGatewayLogin();
      }
    }
  }






  redirectToGatewayLogin() {
    this.AppService.getConfigStatus().subscribe({
      next: (configStatus) => {
        console.log(configStatus);
        if (configStatus) {
          this.appConfig = this.AppService.getGlobalConfig();
          window.location.href = this.appConfig.gatewayLoginUrl;
        }
      },
      error: (error) => console.log(this.errorMessage)
    }
    );
  }

  loadCookieConsentScript() {
    this.isonetFeature.subscribe((featureFlag) => {
      if (featureFlag && featureFlag['enableOneTrustScript']) {
        const script = this.renderer.createElement('script');
        script.type = 'text/javascript';
        script.src = `${environment.oneTrustScriptURl}`;
        script.charset = 'UTF-8';
        script.setAttribute('data-document-language', 'true');
        script.setAttribute('data-domain-script', `${environment.oneTrustScriptId}`);
        this.renderer.appendChild(document.head, script);

        script.onload = () => {
          const inlineScript = this.renderer.createElement('script');
          inlineScript.type = 'text/javascript';
          inlineScript.text = `function OptanonWrapper() { }`;
          this.renderer.appendChild(document.head, inlineScript);
        };
      }
    });
  }

  private callAppLogout(token: string) {
    const data = { token: token };
    this.http.post(`${environment.apiUrl}logout`, data).subscribe();
  }
}
